import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, Delete, HelpOutline } from '@mui/icons-material';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';

interface DocumentField {
  id: string;
  name?: string; // User-defined name
  type: 'text' | 'textarea' | 'checkbox' | 'radio' | 'select' | 'date' | 'file' | 'number' | 'array' | 'calculation' | 'dropdown';
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  arrayFields?: DocumentField[]; // For array type fields
  calculation?: string; // For calculation type fields
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  value?: string; // Default value
  others?: string; // Additional field for "others" column
  tooltip?: string;
}

interface FormSection {
  id: string;
  title: string;
  type?: 'table' | 'fields';
  fields: DocumentField[];
  isRepeatable?: boolean;
  description?: string;
}

interface DocumentConfig {
  _id?: string;
  title: string;
  description: string;
  header?: string; // Fixed header content
  footer?: string; // Fixed footer content
  templateText?: string;
  sections: FormSection[];
  titleFields?: DocumentField[];
  acknowledgement?: string;
  declaration?: string;
}

interface DocumentRendererProps {
  documentConfig: DocumentConfig;
  initialValues?: Record<string, any>;
  readOnlyFields?: string[]; // Field IDs that should be read-only (pre-filled from questionnaires)
  onSubmit: (data: any) => void | Promise<void>;
}

const TEMPLATE_PLACEHOLDER_REGEX = /\[([^\]]+)\]/g;

const getTemplatePlaceholders = (template: string) => {
  TEMPLATE_PLACEHOLDER_REGEX.lastIndex = 0;
  const keys: string[] = [];
  const seen = new Set<string>();
  let match = TEMPLATE_PLACEHOLDER_REGEX.exec(template);
  while (match) {
    const key = match[1].trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
    match = TEMPLATE_PLACEHOLDER_REGEX.exec(template);
  }
  return keys;
};

const renderBoldSegments = (text: string, keyPrefix: string) => {
  const parts = text.split('**');
  if (parts.length === 1) {
    return [<Box component="span" key={`${keyPrefix}-plain`}>{text}</Box>];
  }
  return parts.map((part, index) => {
    const isBold = index % 2 === 1;
    return (
      <Box
        component={isBold ? 'strong' : 'span'}
        key={`${keyPrefix}-bold-${index}`}
        sx={{ fontWeight: isBold ? 700 : 400 }}
      >
        {part}
      </Box>
    );
  });
};

// Array Field Renderer Component
const ArrayFieldRenderer: React.FC<{ field: DocumentField; control: any; errors: any }> = ({ field, control }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: field.id
  });

  const addItem = () => {
    const newItem = field.arrayFields?.reduce((acc, subField) => ({
      ...acc,
      [subField.id]: subField.type === 'number' ? 0 : ''
    }), {}) || {};
    append(newItem);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Section Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {field.label}
        </Typography>
        <Button
          startIcon={<Add />}
          onClick={addItem}
          variant="text"
          sx={{
            color: 'primary.main',
            textTransform: 'uppercase',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          ADD {field.label.toUpperCase()}
        </Button>
      </Box>

      {/* Array Items */}
      {fields.map((item, index) => (
        <Paper
          key={item.id}
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            '&:hover': {
              borderColor: '#bdbdbd'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Delete Button */}
            <IconButton
              onClick={() => remove(index)}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'error.main' }
              }}
            >
              <Delete />
            </IconButton>

            {/* Input Fields */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 2,
              flex: 1
            }}>
              {field.arrayFields?.map((subField) => (
                <Controller
                  key={subField.id}
                  name={`${field.id}.${index}.${subField.id}`}
                  control={control}
                  defaultValue={subField.type === 'number' ? 0 : ''}
                  render={({ field: controllerField }) => {
                    // Handle dropdown fields
                    if (subField.options && subField.options.length > 0) {
                      return (
                        <FormControl size="small" fullWidth>
                          <InputLabel>{subField.label}</InputLabel>
                          <Select
                            {...controllerField}
                            label={subField.label}
                          >
                            {subField.options.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }

                    // Handle regular input fields
                    return (
                      <TextField
                        {...controllerField}
                        label={subField.label}
                        type={subField.type === 'number' ? 'number' : 'text'}
                        size="small"
                        fullWidth
                        variant="outlined"
                        onChange={(e) => {
                          const value = subField.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
                          controllerField.onChange(value);
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white'
                          }
                        }}
                      />
                    );
                  }}
                />
              ))}
            </Box>
          </Box>
        </Paper>
      ))}

      {/* Add first item if none exist */}
      {fields.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No {field.label.toLowerCase()} added yet
          </Typography>
          <Button
            startIcon={<Add />}
            onClick={addItem}
            variant="outlined"
            size="small"
          >
            Add First {field.label}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Repeatable Section Renderer Component
const RepeatableSectionRenderer: React.FC<{
  section: FormSection;
  control: any;
  renderField: (field: DocumentField, customName?: string) => React.ReactNode;
}> = ({ section, control, renderField }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: section.id
  });


  return (
    <Paper sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {section.title}
          </Typography>
          {section.description && (
            <Typography variant="body2" color="text.secondary">
              {section.description}
            </Typography>
          )}
        </Box>
        <Button
          startIcon={<Add />}
          onClick={() => append({})}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2 }}
        >
          Add more data
        </Button>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {section.fields.map((field) => (
                <TableCell key={field.id} sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                  {field.name || field.label}
                  {field.required && <span style={{ color: 'red' }}> *</span>}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', width: 50 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((item, index) => (
              <TableRow key={item.id}>
                {section.fields.map((field) => (
                  <TableCell key={field.id} sx={{ verticalAlign: 'top', py: 1 }}>
                    {renderField(field, `${section.id}.${index}.${field.id}`)}
                  </TableCell>
                ))}
                <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
                  <IconButton
                    onClick={() => remove(index)}
                    color="error"
                    size="small"
                    disabled={fields.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Calculation Field Renderer Component
const CalculationFieldRenderer: React.FC<{ field: DocumentField; control: any }> = ({ field, control }) => {
  const watchedValues = useWatch({ control });

  const calculateValue = () => {
    try {
      // Simple calculation logic - can be enhanced
      if (field.calculation) {
        // Replace field names with actual values in the calculation
        let calculation = field.calculation;
        Object.keys(watchedValues).forEach(key => {
          const value = watchedValues[key];
          if (Array.isArray(value)) {
            // Sum array values if they contain numeric fields
            const sum = value.reduce((acc, item) => {
              if (typeof item === 'object') {
                return acc + Object.values(item).reduce((subAcc: number, subVal: any) =>
                  subAcc + (typeof subVal === 'number' ? subVal : 0), 0);
              }
              return acc + (typeof item === 'number' ? item : 0);
            }, 0);
            calculation = calculation.replace(new RegExp(`\\b${key}\\b`, 'g'), sum.toString());
          } else if (typeof value === 'number') {
            calculation = calculation.replace(new RegExp(`\\b${key}\\b`, 'g'), value.toString());
          }
        });

        // Evaluate simple mathematical expressions safely
        // eslint-disable-next-line no-new-func
        const result = Function(`"use strict"; return (${calculation})`)();
        return isNaN(result) ? 0 : result;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Calculation error:', error);
    }
    return 0;
  };

  const calculatedValue = calculateValue();

  return (
    <TextField
      label={field.label}
      value={`$${calculatedValue.toLocaleString()}`}
      fullWidth
      size="small"
      InputProps={{ readOnly: true }}
      sx={{
        '& .MuiInputBase-input': {
          fontWeight: 'bold',
          color: 'primary.main'
        }
      }}
    />
  );
};

const DocumentRenderer: React.FC<DocumentRendererProps> = ({ documentConfig, initialValues, readOnlyFields = [], onSubmit }) => {
  const readOnlyFieldSet = React.useMemo(() => new Set(readOnlyFields), [readOnlyFields]);
  const isDocumentTemplate = Boolean(documentConfig.templateText);
  const templatePlaceholders = React.useMemo(() => (
    documentConfig.templateText ? getTemplatePlaceholders(documentConfig.templateText) : []
  ), [documentConfig.templateText]);
  const templateFields = React.useMemo(() => {
    if (!isDocumentTemplate) return [];
    const fields = (documentConfig.sections || []).flatMap(section => section.fields || []);
    const fieldMap = new Map(fields.map(field => [field.id, field]));
    return templatePlaceholders.map((key) => fieldMap.get(key) || ({
      id: key,
      name: key,
      label: key,
      type: 'text',
      required: false
    } as DocumentField));
  }, [documentConfig.sections, isDocumentTemplate, templatePlaceholders]);
  const templateFieldMap = React.useMemo(() => (
    new Map(templateFields.map((field) => [field.id, field]))
  ), [templateFields]);

  // Initialize repeatable sections with one empty row
  const defaultValues = React.useMemo(() => {
    const defaults: Record<string, any> = { ...(initialValues || {}) };

    if (isDocumentTemplate) {
      templateFields.forEach((field) => {
        if (defaults[field.id] === undefined) {
          defaults[field.id] = field.type === 'number' ? '' : (field.value || '');
        }
      });
      return defaults;
    }

    (documentConfig.sections || []).forEach(section => {
      if (section.isRepeatable) {
        const existingValues = defaults[section.id];
        if (!Array.isArray(existingValues) || existingValues.length === 0) {
          defaults[section.id] = [{}];
        }
      }
    });

    return defaults;
  }, [documentConfig.sections, initialValues, isDocumentTemplate, templateFields]);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const isEditMode = initialValues !== undefined;

  const buildFieldRules = (field: DocumentField) => ({
    required: field.required ? `${field.name || field.label} is required` : false,
    ...(field.validation?.min && { min: { value: field.validation.min, message: `Minimum value is ${field.validation.min}` } }),
    ...(field.validation?.max && { max: { value: field.validation.max, message: `Maximum value is ${field.validation.max}` } }),
    ...(field.validation?.pattern && { pattern: { value: new RegExp(field.validation.pattern), message: 'Invalid format' } })
  });

  const inlineFieldContainerSx = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    mx: 0.4,
    verticalAlign: 'middle'
  } as const;

  const getInlineInputSx = (hasError: boolean) => ({
    '& .MuiInputBase-root': {
      fontFamily: 'inherit',
      fontSize: '0.98rem',
      lineHeight: 1.7,
      backgroundColor: 'rgba(61, 90, 91, 0.06)',
      borderRadius: '6px'
    },
    '& .MuiInputBase-input': {
      padding: '4px 6px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderStyle: 'dashed',
      borderColor: hasError ? 'rgba(198, 40, 40, 0.7)' : 'rgba(31, 26, 18, 0.25)'
    }
  });

  const renderInlineError = (message?: string) => {
    if (!message) return null;
    return (
      <Typography
        component="span"
        sx={{
          color: 'error.main',
          fontSize: '0.7rem',
          lineHeight: 1.2,
          mt: 0.2,
          fontFamily: 'inherit'
        }}
      >
        {message}
      </Typography>
    );
  };

  const renderInlineField = (field: DocumentField, key: string) => {
    const fieldName = field.id;
    const isReadOnly = readOnlyFieldSet.has(fieldName);
    const fieldProps = {
      name: fieldName,
      control,
      defaultValue: field.type === 'number' ? '' : (field.value || ''),
      rules: isReadOnly ? {} : buildFieldRules(field) // Skip validation for read-only fields
    };

    // Read-only styles - show a subtle lock/disabled appearance
    const getReadOnlyInputSx = () => ({
      '& .MuiInputBase-root': {
        fontFamily: 'inherit',
        fontSize: '0.98rem',
        lineHeight: 1.7,
        backgroundColor: 'rgba(76, 175, 80, 0.08)', // Light green for pre-filled
        borderRadius: '6px'
      },
      '& .MuiInputBase-input': {
        padding: '4px 6px',
        color: '#2e7d32', // Green text for pre-filled values
        fontWeight: 600
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderStyle: 'solid',
        borderColor: 'rgba(76, 175, 80, 0.4)' // Green border for pre-filled
      },
      '& .Mui-disabled': {
        WebkitTextFillColor: '#2e7d32',
        cursor: 'not-allowed'
      }
    });

    if (field.type === 'select' || field.type === 'dropdown') {
      const options = field.options || [];
      return (
        <Box component="span" key={key} sx={inlineFieldContainerSx}>
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <>
                <TextField
                  {...controllerField}
                  select
                  size="small"
                  variant="outlined"
                  error={!isReadOnly && !!error}
                  disabled={isReadOnly}
                  SelectProps={{ displayEmpty: true }}
                  sx={{ minWidth: 140, ...(isReadOnly ? getReadOnlyInputSx() : getInlineInputSx(!!error)) }}
                >
                  <MenuItem value="">
                    <em>{field.label}</em>
                  </MenuItem>
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                {!isReadOnly && renderInlineError(error?.message)}
              </>
            )}
          />
        </Box>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <Box component="span" key={key} sx={inlineFieldContainerSx}>
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: `1px ${isReadOnly ? 'solid' : 'dashed'} ${isReadOnly
                        ? 'rgba(76, 175, 80, 0.4)'
                        : error
                          ? 'rgba(198, 40, 40, 0.7)'
                          : 'rgba(31, 26, 18, 0.25)'
                      }`,
                    backgroundColor: isReadOnly ? 'rgba(76, 175, 80, 0.08)' : 'rgba(61, 90, 91, 0.06)'
                  }}
                >
                  <Checkbox
                    checked={Boolean(controllerField.value)}
                    onChange={(event) => !isReadOnly && controllerField.onChange(event.target.checked)}
                    disabled={isReadOnly}
                    size="small"
                    inputProps={{ 'aria-label': field.label }}
                  />
                </Box>
                {!isReadOnly && renderInlineError(error?.message)}
              </>
            )}
          />
        </Box>
      );
    }

    const isMultiline = field.type === 'textarea';
    const inputType = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';

    return (
      <Box component="span" key={key} sx={inlineFieldContainerSx}>
        <Controller
          {...fieldProps}
          render={({ field: controllerField, fieldState: { error } }) => (
            <>
              <TextField
                {...controllerField}
                size="small"
                variant="outlined"
                type={inputType}
                placeholder={isReadOnly ? '' : (field.placeholder || field.label)}
                multiline={isMultiline}
                minRows={isMultiline ? 2 : undefined}
                error={!isReadOnly && !!error}
                disabled={isReadOnly}
                sx={{ minWidth: isMultiline ? 220 : 120, ...(isReadOnly ? getReadOnlyInputSx() : getInlineInputSx(!!error)) }}
                inputProps={{ 'aria-label': field.label }}
              />
              {!isReadOnly && renderInlineError(error?.message)}
            </>
          )}
        />
      </Box>
    );
  };

  // Intentionally a plain function (not useCallback): it closes over renderInlineField, which itself
  // depends on control/readOnlyFieldSet and is redefined every render, so memoizing here would cascade
  // into memoizing renderInlineField's own helpers too, with no behavior change — not worth the risk.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderInlineDocumentSegments = (text: string, keyPrefix: string) => {
    const parts = text.split(/(\[[^\]]+\])/g);
    return parts.flatMap((part, index) => {
      if (!part) return [];
      if (/^\[[^\]]+\]$/.test(part)) {
        const key = part.slice(1, -1).trim();
        const field = templateFieldMap.get(key) || {
          id: key,
          label: key,
          type: 'text',
          required: false
        } as DocumentField;
        return [renderInlineField(field, `${keyPrefix}-ph-${index}`)];
      }
      return renderBoldSegments(part, `${keyPrefix}-txt-${index}`);
    });
  };
  const renderedTemplate = React.useMemo(() => {
    if (!documentConfig.templateText) return null;
    const tokens = documentConfig.templateText.split(/(\n)/);
    return tokens.map((token, index) => {
      if (token === '\n') {
        return <br key={`tpl-br-${index}`} />;
      }
      return (
        <React.Fragment key={`tpl-seg-${index}`}>
          {renderInlineDocumentSegments(token, `tpl-${index}`)}
        </React.Fragment>
      );
    });
  }, [documentConfig.templateText, renderInlineDocumentSegments]);

  React.useEffect(() => {
    if (initialValues !== undefined) {
      reset(defaultValues);
    }
  }, [initialValues, reset, defaultValues]);

  const renderField = (field: DocumentField, customName?: string) => {
    const fieldName = customName || field.id;

    const LabelWithTooltip = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {field.label}
        {field.tooltip && (
          <Tooltip title={field.tooltip} arrow>
            <HelpOutline sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
    );

    const fieldProps = {
      name: fieldName,
      control,
      defaultValue: field.type === 'number' ? '' : (field.value || ''),
      rules: buildFieldRules(field)
    };

    switch (field.type) {
      case 'text':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <TextField
                {...controllerField}
                label={LabelWithTooltip}
                fullWidth
                size="small"
                placeholder={field.placeholder}
                error={!!error}
                helperText={error?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        );

      case 'textarea':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <TextField
                {...controllerField}
                label={LabelWithTooltip}
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder={field.placeholder}
                error={!!error}
                helperText={error?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        );

      case 'number':
        return (
          <Controller
            {...fieldProps}
            defaultValue=""
            render={({ field: controllerField, fieldState: { error } }) => (
              <TextField
                {...controllerField}
                label={LabelWithTooltip}
                fullWidth
                type="number"
                size="small"
                placeholder={field.placeholder || "Enter amount"}
                error={!!error}
                helperText={error?.message}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: field.validation?.min,
                  max: field.validation?.max
                }}
              />
            )}
          />
        );

      case 'date':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <TextField
                {...controllerField}
                label={LabelWithTooltip}
                fullWidth
                type="date"
                size="small"
                error={!!error}
                helperText={error?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        );

      case 'checkbox':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...controllerField}
                      checked={controllerField.value || false}
                      size="small"
                    />
                  }
                  label={LabelWithTooltip}
                />
                {error && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {error.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        );

      case 'radio':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <FormControl component="fieldset" error={!!error} size="small">
                <RadioGroup {...controllerField} row>
                  {field.options?.map((option) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={<Radio size="small" />}
                      label={option}
                    />
                  ))}
                </RadioGroup>
                {error && (
                  <Typography variant="caption" color="error">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        );

      case 'dropdown':
      case 'select':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error} size="small">
                <InputLabel shrink>{LabelWithTooltip}</InputLabel>
                <Select {...controllerField} displayEmpty label={LabelWithTooltip}>
                  <MenuItem value="">
                    <em>Select an option</em>
                  </MenuItem>
                  {field.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography variant="caption" color="error">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        );

      case 'file':
        return (
          <Controller
            {...fieldProps}
            render={({ field: controllerField, fieldState: { error } }) => (
              <TextField
                type="file"
                fullWidth
                size="small"
                onChange={(e) => {
                  const files = (e.target as HTMLInputElement).files;
                  controllerField.onChange(files?.[0]);
                }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        );

      case 'array':
        return (
          <ArrayFieldRenderer
            field={field}
            control={control}
            errors={errors}
          />
        );

      case 'calculation':
        return (
          <CalculationFieldRenderer
            field={field}
            control={control}
          />
        );

      default:
        return null;
    }
  };

  const onFormSubmit = async (data: any) => {
    setSubmitting(true);
    try {
      if (isDocumentTemplate) {
        await onSubmit(data);
        if (isEditMode) {
          setSubmitMessage('Responses updated successfully.');
        }
        return;
      }

      // Transform data to use section names instead of IDs
      const transformedData: Record<string, any> = {};

      // Handle titleFields first
      if (documentConfig.titleFields) {
        documentConfig.titleFields.forEach((field: DocumentField) => {
          if (Object.prototype.hasOwnProperty.call(data, field.id)) {
            const fieldKey = field.name || field.label;
            transformedData[fieldKey] = data[field.id];
          }
        });
      }

      // Transform sections to use section names instead of IDs
      if (documentConfig.sections) {
        documentConfig.sections.forEach((section: FormSection) => {
          const sectionKey = section.title; // Keeping the original title as key for display consistency

          if (section.isRepeatable && Array.isArray(data[section.id])) {
            transformedData[sectionKey] = data[section.id].map((row: any) => {
              const rowData: any = {};
              section.fields.forEach((field: DocumentField) => {
                const fieldKey = field.name || field.label;
                rowData[fieldKey] = row[field.id];
              });
              return rowData;
            });
          } else {
            const sectionData: any = {};
            if (section.fields) {
              section.fields.forEach((field: DocumentField) => {
                if (Object.prototype.hasOwnProperty.call(data, field.id)) {
                  const fieldKey = field.name || field.label;
                  sectionData[fieldKey] = data[field.id];
                }
              });
            }
            if (Object.keys(sectionData).length > 0) {
              transformedData[sectionKey] = sectionData;
            }
          }
        });
      }

      await onSubmit(transformedData);
      if (isEditMode) {
        setSubmitMessage('Document updated successfully.');
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Document submission error:', error);
      if (error.response?.status === 403) {
        setSubmitMessage('Access denied. Please ensure you have the required permissions.');
      } else if (error.response?.status === 404) {
        setSubmitMessage('Document not found. Please try again.');
      } else {
        setSubmitMessage('Failed to create document. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const generatePDF = (data: any) => {
    const reportWindow = window.open('', '_blank');
    if (!reportWindow) return;

    const escapeHtml = (v: unknown): string => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const formatValue = (value: any, field: DocumentField) => {
      if (field.type === 'array' && Array.isArray(value)) {
        return value.map((item, index) => `
          <tr>
            <td colspan="3"><strong>Item ${index + 1}</strong></td>
          </tr>
          ${field.arrayFields?.map(subField => `
            <tr>
              <td style="padding-left: 20px;">${escapeHtml(subField.label)}</td>
              <td>${escapeHtml(item[subField.id] || 'N/A')}</td>
              <td></td>
            </tr>
          `).join('') || ''}
        `).join('');
      } else if (field.type === 'calculation') {
        return escapeHtml(`$${(value || 0).toLocaleString()}`);
      } else if (field.type === 'checkbox') {
        return value ? 'Yes' : 'No';
      }
      return escapeHtml(value || 'N/A');
    };

    reportWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${escapeHtml(documentConfig.title)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .section { margin-bottom: 25px; }
          .section h3 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .amount { text-align: right; }
          .total { font-weight: bold; background-color: #f0f0f0; }
          .disclaimer { margin-top: 30px; padding: 15px; background-color: #fff3cd; border: 1px solid #ffeaa7; font-size: 12px; }
          .signature-section { margin-top: 40px; border-top: 2px solid #333; padding-top: 20px; }
          .signature-box { display: inline-block; width: 200px; margin: 20px; text-align: center; }
          .signature-line { border-bottom: 1px solid #333; height: 40px; margin-bottom: 5px; }
        </style>
      </head>
      <body>
        ${documentConfig.header ? `
        <div class="header">
          <div style="white-space: pre-wrap;">${escapeHtml(documentConfig.header)}</div>
        </div>
        ` : ''}

        <div class="header">
          <h1>${escapeHtml(documentConfig.title.toUpperCase())}</h1>
          <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
        </div>

        ${documentConfig.sections.map(section => {
      const sectionValue = data[section.id];
      const isRepeatable = section.isRepeatable && Array.isArray(sectionValue);

      return `
        <div class="section">
          <h3>${escapeHtml(section.title.toUpperCase())}</h3>
          <table>
            <thead>
              <tr>
                ${section.fields.map(f => `<th>${escapeHtml(f.label)}</th>`).join('')}
                ${!isRepeatable ? '<th>Notes</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${isRepeatable ?
          sectionValue.map((row: any) => `
                  <tr>
                    ${section.fields.map(f => `<td>${formatValue(row[f.id], f)}</td>`).join('')}
                  </tr>
                `).join('') :
          `<tr>
                   ${section.fields.map(field => `<td>${formatValue(data[field.id], field)}</td>`).join('')}
                   <td></td>
                 </tr>`
        }
            </tbody>
          </table>
        </div>
      `;
    }).join('')}

        ${documentConfig.footer ? `
        <div class="section">
          <div style="white-space: pre-wrap;">${escapeHtml(documentConfig.footer)}</div>
        </div>
        ` : ''}

        <div class="signature-section">
          <h3>SIGNATURES</h3>
          <div style="display: flex; justify-content: space-around; margin-top: 40px;">
            <div class="signature-box">
              <div class="signature-line"></div>
              <p><strong>Testator Signature</strong></p>
              <p>Date: _______________</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p><strong>Witness 1 Signature</strong></p>
              <p>Date: _______________</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p><strong>Witness 2 Signature</strong></p>
              <p>Date: _______________</p>
            </div>
          </div>
        </div>

        <div class="disclaimer">
          <strong>DISCLAIMER:</strong> This document was generated using a document builder tool.
          It is not legal advice. Consult a qualified legal professional for estate planning.
        </div>
      </body>
      </html>
    `);

    reportWindow.document.close();
    setTimeout(() => {
      reportWindow.print();
    }, 500);
  };

  if (isDocumentTemplate) {
    const documentSubmitLabel = submitting
      ? (isEditMode ? 'Updating...' : 'Saving...')
      : (isEditMode ? 'Update Responses' : 'Save Responses');

    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
        {submitMessage && (
          <Alert
            severity={submitMessage.includes('success') ? 'success' : 'error'}
            sx={{ mb: 3 }}
            onClose={() => setSubmitMessage('')}
          >
            {submitMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#fffaf3', border: '1px solid #efe7d8' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Fill in the document
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Edit the highlighted fields directly in the will.
            </Typography>
            <Box
              sx={{
                border: '1px solid rgba(31, 26, 18, 0.1)',
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                fontFamily: '"Garamond", "Georgia", serif',
                fontSize: '0.98rem',
                lineHeight: 1.7,
                color: '#1f1a12',
                minHeight: 260,
                whiteSpace: 'pre-wrap',
                backgroundColor: '#fff'
              }}
            >
              {documentConfig.templateText ? renderedTemplate : (
                <Typography variant="body2" color="text.secondary">
                  Template preview is not available.
                </Typography>
              )}
            </Box>
          </Paper>

          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mt: 3 }}>
              Please correct the validation errors in the form before submitting.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{ minWidth: 220 }}
            >
              {documentSubmitLabel}
            </Button>
          </Box>
        </form>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>

      {submitMessage && (
        <Alert
          severity={submitMessage.includes('success') ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setSubmitMessage('')}
        >
          {submitMessage}
        </Alert>
      )}

      {/* Header */}
      {documentConfig.header && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {documentConfig.header}
          </Typography>
        </Paper>
      )}

      <form onSubmit={handleSubmit(onFormSubmit)}>
        {/* Title Fields */}
        {documentConfig.titleFields && documentConfig.titleFields.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
              {documentConfig.titleFields.map((field) => (
                <Box key={field.id}>
                  {renderField(field)}
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {documentConfig.sections.map((section) => {
          if (section.isRepeatable) {
            return (
              <RepeatableSectionRenderer
                key={section.id}
                section={section}
                control={control}
                renderField={renderField}
              />
            );
          }

          return (
            <Paper key={section.id} sx={{ mb: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
                {section.description && (
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                )}
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {section.fields.map((field) => (
                        <TableCell key={field.id} sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                          {field.name || field.label}
                          {field.required && <span style={{ color: 'red' }}> *</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {section.fields.map((field) => (
                        <TableCell key={field.id} sx={{ verticalAlign: 'top' }}>
                          {renderField(field)}
                          {field.others && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {field.others}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          );
        })}

        {/* Acknowledgement and Declaration */}
        {(documentConfig.acknowledgement || documentConfig.declaration) && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            {documentConfig.acknowledgement && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {documentConfig.acknowledgement}
              </Typography>
            )}
            {documentConfig.declaration && (
              <Typography variant="body1">
                {documentConfig.declaration}
              </Typography>
            )}
          </Paper>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Please correct the validation errors in the form before submitting.
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{ minWidth: 200 }}
          >
            {submitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Document' : 'Submit Document')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleSubmit(generatePDF)}
            sx={{ minWidth: 200 }}
          >
            Download PDF
          </Button>
        </Box>
      </form>

      {/* Footer */}
      {documentConfig.footer && (
        <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50', borderLeft: 4, borderColor: 'grey.400' }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {documentConfig.footer}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DocumentRenderer;
